import { useState } from 'react';
import { X, Send, Loader2, CheckCircle, Calendar, Users, Phone, Mail, User } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { publicApi } from '../../lib/api';
import { cn, formatPrice } from '../../lib/utils';

export function EnquiryModal({ isOpen, onClose, tour }) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    travel_date: '',
    adults: 2,
    children: 0,
    message: ''
  });
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Invalid email';
    if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
    if (!/^[\d\s+-]{10,}$/.test(formData.phone)) newErrors.phone = 'Invalid phone';
    if (!formData.travel_date) newErrors.travel_date = 'Travel date is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      await publicApi.createEnquiry({
        ...formData,
        package_id: tour?.id,
        package_name: tour?.name,
        destination: tour?.destination,
        number_of_travelers: {
          adults: parseInt(formData.adults),
          children: parseInt(formData.children)
        }
      });
      setIsSuccess(true);
      setTimeout(() => {
        onClose();
        setIsSuccess(false);
        setFormData({
          name: '',
          email: '',
          phone: '',
          travel_date: '',
          adults: 2,
          children: 0,
          message: ''
        });
      }, 2500);
    } catch (error) {
      console.error('Error submitting enquiry:', error);
      setErrors({ submit: 'Failed to submit. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={() => !isSubmitting && onClose()}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden animate-scale-in">
        {/* Header with Tour Info */}
        <div className="relative bg-gradient-to-r from-[#004E64] to-[#006B87] p-6 text-white">
          <button
            onClick={() => !isSubmitting && onClose()}
            className="absolute top-4 right-4 p-2 hover:bg-white/20 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          
          {tour && (
            <div className="pr-10">
              <span className="text-white/80 text-sm">
                Enquire About
              </span>
              <h3 
                className="text-xl font-semibold mt-1 line-clamp-2"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                {tour.name}
              </h3>
              <div className="flex items-center gap-4 mt-2 text-white/80 text-sm">
                <span>{tour.destination}</span>
                <span>•</span>
                <span>{tour.duration_days}D/{tour.duration_nights}N</span>
                <span>•</span>
                <span className="font-semibold text-white">
                  {formatPrice(tour.discounted_price || tour.price)}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Success State */}
        {isSuccess ? (
          <div className="p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 animate-scale-in">
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
            <h4 className="text-xl font-semibold text-[#1A1A1A] mb-2">
              Enquiry Submitted!
            </h4>
            <p className="text-[#4A4A4A]">
              Our travel expert will contact you within 24 hours.
            </p>
          </div>
        ) : (
          /* Form */
          <form onSubmit={handleSubmit} className="p-6 space-y-4 max-h-[60vh] overflow-y-auto">
            {/* Name */}
            <div>
              <Label htmlFor="name" className="text-[#1A1A1A] mb-2 flex items-center gap-2">
                <User className="w-4 h-4" /> Full Name *
              </Label>
              <Input
                id="name"
                placeholder="Enter your full name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className={cn('rounded-xl', errors.name && 'border-red-500')}
              />
              {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
            </div>

            {/* Email & Phone Row */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="email" className="text-[#1A1A1A] mb-2 flex items-center gap-2">
                  <Mail className="w-4 h-4" /> Email *
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="your@email.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className={cn('rounded-xl', errors.email && 'border-red-500')}
                />
                {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
              </div>
              <div>
                <Label htmlFor="phone" className="text-[#1A1A1A] mb-2 flex items-center gap-2">
                  <Phone className="w-4 h-4" /> Phone *
                </Label>
                <Input
                  id="phone"
                  placeholder="+91 9181268900"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className={cn('rounded-xl', errors.phone && 'border-red-500')}
                />
                {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
              </div>
            </div>

            {/* Travel Date */}
            <div>
              <Label htmlFor="travel_date" className="text-[#1A1A1A] mb-2 flex items-center gap-2">
                <Calendar className="w-4 h-4" /> Travel Date *
              </Label>
              <Input
                id="travel_date"
                type="date"
                value={formData.travel_date}
                onChange={(e) => setFormData({ ...formData, travel_date: e.target.value })}
                min={new Date().toISOString().split('T')[0]}
                className={cn('rounded-xl', errors.travel_date && 'border-red-500')}
              />
              {errors.travel_date && <p className="text-red-500 text-xs mt-1">{errors.travel_date}</p>}
            </div>

            {/* Travelers */}
            <div>
              <Label className="text-[#1A1A1A] mb-2 flex items-center gap-2">
                <Users className="w-4 h-4" /> Number of Travelers
              </Label>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="adults" className="text-sm text-[#4A4A4A]">Adults</Label>
                  <Input
                    id="adults"
                    type="number"
                    min="1"
                    max="20"
                    value={formData.adults}
                    onChange={(e) => setFormData({ ...formData, adults: e.target.value })}
                    className="rounded-xl"
                  />
                </div>
                <div>
                  <Label htmlFor="children" className="text-sm text-[#4A4A4A]">Children</Label>
                  <Input
                    id="children"
                    type="number"
                    min="0"
                    max="10"
                    value={formData.children}
                    onChange={(e) => setFormData({ ...formData, children: e.target.value })}
                    className="rounded-xl"
                  />
                </div>
              </div>
            </div>

            {/* Message */}
            <div>
              <Label htmlFor="message" className="text-[#1A1A1A] mb-2">Additional Requirements</Label>
              <Textarea
                id="message"
                placeholder="Any special requests or requirements..."
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                rows={3}
                className="rounded-xl resize-none"
              />
            </div>

            {errors.submit && (
              <p className="text-red-500 text-sm text-center bg-red-50 p-3 rounded-xl">{errors.submit}</p>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-[#FF6B35] hover:bg-[#E85A2D] text-white py-6 rounded-xl text-lg"
            >
              {isSubmitting ? (
                <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Submitting...</>
              ) : (
                <><Send className="w-5 h-5 mr-2" /> Submit Enquiry</>
              )}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
